// App.tsx
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ClientContextProvider, useClientContext } from "./context/ClientContext";
import { DataContextProvider, useDataContext } from "./context/DataContext";
import { ViewPreferencesProvider } from "./context/ViewPreferencesContext";
import { DevFeaturesProvider, useDevFeatures } from "./context/DevFeaturesContext";
import { DomainContextProvider, useDomain } from "./context/DomainContext";
import { DevModeContextProvider } from "./context/DevModeContext";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import DashboardPage from "./pages/DashboardPage";
import SchedulePage from "./pages/SchedulePage";
import CompetenciesPage from "./pages/CompetenciesPage";
import JobsPage from "./pages/JobsPage";
import TravelPage from "./pages/TravelPage";
import TimesheetsPage from "./pages/TimesheetsPage";
import SettingsPage from "./pages/SettingsPage";
import { Message } from "./components/Message";

const Protected = ({ children }: { children: JSX.Element }) => {
  const { client } = useClientContext();
  return client ? children : <Navigate to="/" replace />;
};

function LoadingScreen(): JSX.Element {
  const { loadingState, loadingProgress, error } = useDataContext();

  if (loadingState === "error") {
    return (
      <div className="loading-screen loading-screen--error">
        <div className="loading-screen__content">
          <div className="loading-screen__icon">⚠️</div>
          <h2 className="loading-screen__title">Failed to Load Data</h2>
          <Message type="error" text={error || "Failed to load data."} />
          <button
            className="btn button primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="loading-screen">
      <div className="loading-screen__content">
        <div className="loading-screen__spinner" />
        <h2 className="loading-screen__title">Loading Rapid Crews</h2>
        <p className="loading-screen__message">{loadingProgress || "Initializing..."}</p>
      </div>
    </div>
  );
}

function ErrorPage(): JSX.Element {
  const location = useLocation();
  const state = location.state as { message?: string } | null;
  const message = typeof state?.message === "string" && state.message
    ? state.message
    : "Something went wrong";
  return (
    <main className="error-page">
      <h1>Configuration Error</h1>
      <Message type="error" text={message} />
    </main>
  );
}

// The page exists only while the loaded data has timesheet rows; the nav item follows the same flag.
function TimesheetsRoute(): JSX.Element {
  const { hasTimesheets } = useDataContext();
  return hasTimesheets ? <TimesheetsPage /> : <Navigate to="/app/dashboard" replace />;
}

function AppRoutes(): JSX.Element {
  const { endpointsPageAvailable, EndpointsPage } = useDevFeatures();

  return (
    <Routes>
      <Route path="/error" element={<ErrorPage />} />
      <Route path="/" element={<LandingPage />} />

      <Route
        path="/app"
        element={
          <Protected>
            <ViewPreferencesProvider>
              <DataContextProvider>
                <AppContent />
              </DataContextProvider>
            </ViewPreferencesProvider>
          </Protected>
        }
      >
        {/* default to dashboard page */}
        <Route index element={<Navigate to="/app/dashboard" replace />} />

        {/* additional pages */}
        <Route path="dashboard" element={<DashboardPage />} />
        {endpointsPageAvailable && EndpointsPage && (
          <Route path="endpoints" element={<EndpointsPage />} />
        )}
        <Route path="schedule" element={<SchedulePage />} />
        <Route path="competencies" element={<CompetenciesPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="travel" element={<TravelPage />} />
        <Route path="timesheets" element={<TimesheetsRoute />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  );
}

function AppContent(): JSX.Element {
  const { loadingState } = useDataContext();

  if (loadingState !== "success") {
    return <LoadingScreen />;
  }

  return <Dashboard />;
}

// Wait for domain config to load before rendering the app
// This prevents the flash of default colors
function AppWithDomainReady(): JSX.Element {
  const { isLoading } = useDomain();

  // Show nothing while domain config is loading - prevents color flash
  if (isLoading) {
    return <></>;
  }

  return (
    <ClientContextProvider>
      <DevFeaturesProvider>
        <AppRoutes />
      </DevFeaturesProvider>
    </ClientContextProvider>
  );
}

export default function App() {
  return (
    <DevModeContextProvider>
      <DomainContextProvider>
        <AppWithDomainReady />
      </DomainContextProvider>
    </DevModeContextProvider>
  );
}
